import { useCallback, useEffect, useState } from "react";

import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import PageMeta from "@/components/common/PageMeta";
import DateRangeFilter, { getDefaultDateRange } from "@/components/common/DateRangeFilter";
import { handleApiError } from "@/services/api";
import { reportInr } from "@/services/financialReportsService";
import { eggGradingService, type EggGradingReport } from "@/services/eggGradingService";

/**
 * What the farm's eggs are worth once they have been graded.
 *
 * See docs/architecture/egg-grading.md.
 */
export default function EggGradingPage() {
  const [data, setData] = useState<EggGradingReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [range, setRange] = useState(() => getDefaultDateRange());

  const load = useCallback(async (from: string, to: string) => {
    setLoading(true);
    setError("");
    try {
      setData(await eggGradingService.report(from || undefined, to || undefined));
    } catch (err) {
      // Surfaced, never swallowed: a blank report that cannot say why is the
      // defect this codebase has had to fix on the dashboard twice.
      setError(handleApiError(err));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(range.from, range.to);
  }, [load, range]);

  const nothingGraded = data !== null && data.graded_eggs === 0;

  return (
    <>
      <PageMeta title="Egg Grading | Poultry Book" description="Grade mix and what the downgrades cost" />
      <PageBreadcrumb pageTitle="Egg Grading" />

      <div className="mb-4">
        <DateRangeFilter onApply={(from, to) => setRange({ from, to })} />
      </div>

      {error && (
        <div className="mb-4 rounded-2xl border border-error-200 bg-error-50 p-4 text-sm text-error-600 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
          {error}
        </div>
      )}

      {loading && <p className="py-10 text-center text-sm text-gray-400">Loading grade mix...</p>}

      {!loading && nothingGraded && (
        <div className="card p-6">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            No eggs were graded in this period.
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {data.ungraded_eggs > 0
              ? `${data.ungraded_eggs.toLocaleString("en-IN")} eggs were recorded across ${data.ungraded_days} day${data.ungraded_days === 1 ? "" : "s"}, none of them graded — so there is nothing to compare. Count the cracked, dirty, double-yolk and floor eggs on the Egg Collection form and this fills in.`
              : "No egg production was recorded in this period either."}
          </p>
        </div>
      )}

      {!loading && data && !nothingGraded && (
        <>
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="card p-5 sm:p-6">
              <p className="text-xs text-gray-500 dark:text-gray-400">What the downgrades cost</p>
              <p className="mt-1 text-2xl font-bold text-gray-800 dark:text-white/90">
                {data.table_rate > 0 ? reportInr(data.downgrade_cost) : "—"}
              </p>
              <p className="mt-1 text-xs text-gray-400">
                {data.table_rate > 0
                  ? `at ₹${data.table_rate.toFixed(2)}/egg, downgrades fetching ${Math.round(data.cracked_price_factor * 100)}% of the table rate`
                  : data.table_rate_source}
              </p>
            </div>
            <div className="card p-5 sm:p-6">
              <p className="text-xs text-gray-500 dark:text-gray-400">Graded</p>
              <p className="mt-1 text-2xl font-bold text-gray-800 dark:text-white/90">
                {data.graded_eggs.toLocaleString("en-IN")}
              </p>
              <p className="mt-1 text-xs text-gray-400">
                eggs across {data.graded_days} day{data.graded_days === 1 ? "" : "s"}
              </p>
            </div>
            <div className="card p-5 sm:p-6">
              <p className="text-xs text-gray-500 dark:text-gray-400">Not graded</p>
              <p className="mt-1 text-2xl font-bold text-gray-800 dark:text-white/90">
                {data.ungraded_eggs.toLocaleString("en-IN")}
              </p>
              <p className="mt-1 text-xs text-gray-400">
                {data.ungraded_eggs > 0
                  ? `eggs across ${data.ungraded_days} day${data.ungraded_days === 1 ? "" : "s"} — the mix below says nothing about these`
                  : "every recorded day was graded"}
              </p>
            </div>
          </div>

          <div className="card">
            <div className="px-5 pt-5 pb-3">
              <h3 className="text-base font-semibold text-gray-800 dark:text-white/90">Grade mix</h3>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                Shares are of the {data.graded_eggs.toLocaleString("en-IN")} eggs that were graded,
                not of everything produced. Table and double-yolk fetch the full rate.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-y border-gray-100 text-left text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400">
                  <tr>
                    <th className="px-5 py-2 font-medium">Grade</th>
                    <th className="px-3 py-2 text-right font-medium">Eggs</th>
                    <th className="px-3 py-2 text-right font-medium">Share</th>
                    <th className="px-5 py-2 text-right font-medium">Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {data.grades.map((g) => (
                    <tr key={g.grade} className="text-gray-700 dark:text-gray-300">
                      <td className="px-5 py-3 font-medium text-gray-800 dark:text-white/90">{g.label}</td>
                      <td className="px-3 py-3 text-right tabular-nums">
                        {g.eggs.toLocaleString("en-IN")}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums">{g.share_pct.toFixed(1)}%</td>
                      <td className="px-5 py-3 text-right text-xs">
                        {g.fetches_full_rate ? (
                          <span className="text-gray-500 dark:text-gray-400">full</span>
                        ) : (
                          <span className="text-warning-600 dark:text-warning-400">
                            {Math.round(data.cracked_price_factor * 100)}%
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.cost_is_modelled && (
              <p className="border-t border-gray-100 px-5 py-3 text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400">
                The cost is what this mix is <strong>worth</strong>, not what was banked: egg sales
                do not record a grade, so the figure prices the mix at the farm's own rate rather
                than measuring what each crate actually fetched.
              </p>
            )}
          </div>
        </>
      )}
    </>
  );
}
